import axios from 'axios';
import { API_BASE_URL } from '../config';

const API_URL = `${API_BASE_URL}/api`;

class EInvoiceService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/einvoice`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Add request interceptor for authentication
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // E-Invoice Document Management
  async createEInvoice(invoiceData) {
    try {
      const response = await this.api.post('/', invoiceData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getEInvoice(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAllEInvoices(params = {}) {
    try {
      const response = await this.api.get('/', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateEInvoiceStatus(eInvoiceId, status) {
    try {
      const response = await this.api.put(`/${eInvoiceId}`, { status });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteEInvoice(eInvoiceId) {
    try {
      const response = await this.api.delete(`/${eInvoiceId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // LHDN Integration
  async submitToLHDN(eInvoiceId) {
    try {
      const response = await this.api.post(`/submit`, { eInvoiceId });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getStatusFromLHDN(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDocumentStatus(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async cancelEInvoice(eInvoiceId, cancelData) {
    try {
      const response = await this.api.post(`/${eInvoiceId}/cancel`, cancelData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // QR Code Management
  async getQRCode(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/qr-code`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async generateQRCode(eInvoiceId) {
    try {
      const response = await this.api.post(`/${eInvoiceId}/generate-qr`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Recipient Management (Public endpoints - no auth required)
  async getRecipientByQRToken(qrToken) {
    try {
      // Use base axios instance without auth for public endpoints
      const response = await axios.get(`${API_BASE_URL}/einvoice/recipients/by-token/${qrToken}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateRecipientInfo(qrToken, recipientData) {
    try {
      // Use base axios instance without auth for public endpoints
      const payload = {
        recipientType: recipientData.recipientType === 'company' ? 'BUSINESS' : 'INDIVIDUAL',
        companyName: recipientData.companyName || '',
        firstName: recipientData.firstName || '',
        lastName: recipientData.lastName || '',
        email: recipientData.email,
        phoneNumber: recipientData.phone,
        addressLine1: recipientData.address,
        addressLine2: recipientData.addressLine2 || '',
        postalCode: recipientData.postalCode,
        city: recipientData.city,
        state: recipientData.state,
        country: recipientData.country || 'MY',
        qrCodeToken: qrToken
      };
      const response = await axios.post(`${API_URL}/einvoice/recipients`, payload, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Status and History
  async getStatusHistory(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/status-history`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAccessLogs(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/access-logs`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Configuration
  async getConfig() {
    try {
      const response = await this.api.get('/config');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateConfig(configData) {
    try {
      const response = await this.api.put('/config', configData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Reports and Analytics
  async getReports(params = {}) {
    try {
      const response = await this.api.get('/reports', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAnalytics(params = {}) {
    try {
      const response = await this.api.get('/analytics', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Reporting
  async getStatusSummary(dateFrom, dateTo) {
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await this.api.get('/status-summary', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Batch Operations
  async bulkSubmit(eInvoiceIds) {
    try {
      const response = await this.api.post('/bulk/submit', { eInvoiceIds });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async bulkCancel(eInvoiceIds, reason) {
    try {
      const response = await this.api.post('/bulk/cancel', { eInvoiceIds, reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async bulkUpdateStatus(eInvoiceIds, status) {
    try {
      const response = await this.api.post('/bulk/update-status', { eInvoiceIds, status });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Search and Filter
  async searchEInvoices(searchParams) {
    try {
      const response = await this.api.post('/search', searchParams);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Validation
  async validateEInvoice(eInvoiceData) {
    try {
      const response = await this.api.post('/validate', eInvoiceData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Utility Methods
  async downloadEInvoice(eInvoiceId, format = 'pdf') {
    try {
      const response = await this.api.get(`/${eInvoiceId}/download`, {
        params: { format },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `einvoice_${eInvoiceId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      try {
        const data = await this.getEInvoice(eInvoiceId);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `einvoice_${eInvoiceId}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (fallbackError) {
        throw this.handleError(error);
      }
    }
  }

  async previewEInvoice(eInvoiceId) {
    try {
      const response = await this.api.get(`/${eInvoiceId}/preview`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Error handling
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const message = data?.message || data?.error || `HTTP Error ${status}`;
      
      return {
        status,
        message,
        details: data?.details || null,
        validationErrors: data?.validationErrors || null
      };
    } else if (error.request) {
      // Network error
      return {
        status: 0,
        message: 'Network error. Please check your connection and try again.',
        details: null,
        validationErrors: null
      };
    } else {
      // Other error
      return {
        status: -1,
        message: error.message || 'An unexpected error occurred.',
        details: null,
        validationErrors: null
      };
    }
  }
}

export default new EInvoiceService();
